//External libs
import { Injectable } from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from '../common/service';
import UserModel from './user.model';

/**
 * @service UserService
 * @description Service for managing user-related operations within the application.
 * This service handles user profile retrieval, password management (forgot password, update password),
 * and OTP verification. It interacts with the `/user/` backend endpoint.
 *
 * **Key Responsibilities:**
 * 1. **User Details**: Fetching detailed information about a user by ID or mobile number.
 * 2. **Password Recovery**: Initiating the forgot password flow and verifying OTPs.
 * 3. **Password Updates**: Allowing users to change their passwords securely.
 * 4. **Resource Management**: Handling HTTP request lifecycles to prevent memory leaks.
 *
 * **Data Flow:**
 * - **Get Details**: Component requests user info -> Service calls API -> Returns `UserModel`.
 * - **Forgot Password**: Component sends username/dialing code -> Service calls `forgotpassword` endpoint.
 * - **OTP**: Component sends OTP -> Service verifies via `otpVerification`.
 *
 * @feature 1. Retrieve user details by ID or Mobile Number.
 * @feature 2. Forgot Password functionality.
 * @feature 3. OTP Verification.
 * @feature 4. Update Password.
 * @feature 5. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<UserModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /user/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class UserService extends Service<UserModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/user/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/user/');
  }

  /**
   * @method userDetails
   * @async
   * @description Fetch detailed information for a specific user by their ID.
   *
   * @param {string} userId - The unique identifier of the user.
   * @returns {Promise<UserModel>} Promise resolving to the user details.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * userService.userDetails('user-123').then(user => {
   *   console.log('User Details:', user);
   * });
   */
  public userDetails(userId: string) {
    return new Promise<UserModel>((resolve, reject) => {
      const request = this.httpClient.get<UserModel>(`${this.baseUrl}/details/${userId}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getUserDetailsByNumber
   * @async
   * @description Fetch user details using their mobile number (username).
   *
   * This method uses a POST request to `userName` endpoint and does not require authentication headers
   * (as indicated by `httpOptionsWithoutAuth`), likely used during login or recovery flows.
   *
   * @param {string} mobileNumber - The mobile number (username) of the user.
   * @returns {Promise<UserModel>} Promise resolving to the user details.
   * @throws {string} Rejects with the error message if the request fails.
   * @side-effect
   * - Initiates an HTTP POST request.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getUserDetailsByNumber(mobileNumber) {
    const httpOptionsWithoutAuth = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': '*/*',
        'pushnotificationkey': ''
      })
    };
    return new Promise<UserModel>((resolve, reject) => {
      const request = this.httpClient.post<UserModel>(`${this.baseUrl}${this.serviceUrl}userName`, {
        userName: mobileNumber,
      }, httpOptionsWithoutAuth).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error.error.message);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method forgotPassword
   * @async
   * @description Initiate the forgot password process.
   *
   * Sends a request to generate an OTP or recovery link for the user.
   * Uses `httpOptionsWithoutAuth` as the user is not logged in.
   *
   * @param {string} dialingCode - The country dialing code.
   * @param {string} userName - The username (mobile number).
   * @returns {Promise<UserModel>} Promise resolving to the operation result.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `forgotpassword`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public forgotPassword(dialingCode, userName) {
    const httpOptionsWithoutAuth = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': '*/*',
        'pushnotificationkey': ''
      })
    };
    return new Promise<UserModel>((resolve, reject) => {
      const request = this.httpClient.post<UserModel>(`${this.baseUrl}${this.serviceUrl}forgotpassword`, {
        dialingCode: dialingCode,
        username: userName,
        isBH: false,
        appName: 'BluBay',
        templateNameCode: 'RSTP'
      }, httpOptionsWithoutAuth).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method otpVerification
   * @async
   * @description Verify the One-Time Password (OTP) entered by the user.
   *
   * Uses `httpOptionsWithoutAuth`.
   *
   * @param {string} otp - The OTP code entered by the user.
   * @param {string} userName - The username (mobile number) associated with the OTP.
   * @returns {Promise<UserModel>} Promise resolving to the verification result.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `otpVerification`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public otpVerification(otp, userName) {
    const httpOptionsWithoutAuth = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': '*/*',
        'pushnotificationkey': ''
      })
    };
    return new Promise<UserModel>((resolve, reject) => {
      const request = this.httpClient.post<UserModel>(`${this.baseUrl}${this.serviceUrl}otpVerification`, {
        otp: otp,
        username: userName
      }, httpOptionsWithoutAuth).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method updatePassword
   * @async
   * @description Update the user's password after successful verification.
   *
   * @param {string} password - The new password.
   * @param {string} mobileNumber - The username (mobile number) of the user.
   * @returns {Promise<UserModel>} Promise resolving to the update result.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `updatePassword`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public updatePassword(password, mobileNumber) {
    return new Promise<UserModel>((resolve, reject) => {
      const request = this.httpClient.post<UserModel>(`${this.baseUrl}${this.serviceUrl}updatePassword`, {
        password: password,
        username: mobileNumber,
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
